import { useState, useRef, useCallback, useMemo } from 'react';
import { LegalDocument } from '@/schemas/schema';
import {
  findBlockByText,
  buildDocumentTextIndex,
  type TextBlockMapping,
} from '@/lib/pdf/text-block-mapper';
import { isVirtualBlockId } from '@/lib/pdf/virtual-blocks';
import { fromReviewBlock, toReviewBlock } from '@/lib/pdf/review-outline';
import type { ReviewBlock, ReviewHighlight } from '../adapter/types';

interface UseBlockHoverOptions {
  document: LegalDocument;
  pageRefs: React.RefObject<Map<number, HTMLDivElement>>;
  /** Translated default for the effective-date label (e.g. "Effective Date:"). */
  effectiveDateLabel: string;
  /** When false (document locked / already editing), hover resolution is skipped. */
  enabled?: boolean;
  /**
   * Total rendered page count. The signature section always starts on its own
   * page (forced break), so the last page is signature-only — hover/click
   * editing is suppressed there (its content is edited via the outline).
   */
  totalPages?: number | null;
}

const EFFECTIVE_DATE_PATTERNS = [
  'effective date',
  'commencement date',
  'start date',
  'execution date',
  'date:',
];

/** Calculate word-overlap similarity (0-1) between two strings. */
function calculateSimilarity(text1: string, text2: string): number {
  if (!text1 || !text2) return 0;
  const words1 = new Set(text1.split(/\s+/).filter((w) => w.length > 2));
  const words2 = new Set(text2.split(/\s+/).filter((w) => w.length > 2));
  if (words1.size === 0 || words2.size === 0) return 0;
  let matches = 0;
  words1.forEach((w) => {
    if (words2.has(w)) matches++;
  });
  return matches / Math.max(words1.size, words2.size);
}

/**
 * Finds the document block for a given span and all sibling spans belonging to it.
 * Uses Y-position jumps to detect paragraph boundaries during expansion, which
 * is far more robust than text-include matching against hyphenated PDF.js spans.
 */
function findBlockForSpan(
  spanText: string,
  allSpans: HTMLElement[],
  spanIndex: number,
  document: LegalDocument,
  effectiveDateLabel: string
): { blockMapping: TextBlockMapping; matchingSpanIndices: number[] } | null {
  if (!spanText || spanText.length < 2) return null;

  // --- Effective date special case ---
  const normalizedText = spanText.toLowerCase();
  if (EFFECTIVE_DATE_PATTERNS.some((p) => normalizedText.includes(p))) {
    const currentSpan = allSpans[spanIndex];
    const currentRect = currentSpan.getBoundingClientRect();
    const lineThreshold = 5;
    const lineSpanIndices: number[] = [];
    allSpans.forEach((span, idx) => {
      const rect = span.getBoundingClientRect();
      if (Math.abs(rect.top - currentRect.top) < lineThreshold) {
        lineSpanIndices.push(idx);
      }
    });
    return {
      blockMapping: {
        blockId: 'metadata-effectiveDate',
        blockPath: [],
        text: `${document.metadata.effectiveDateLabel || effectiveDateLabel} ${document.metadata.effectiveDate || ''}`.trim(),
        type: 'paragraph',
        isLeaf: true,
        isTitle: false,
      },
      matchingSpanIndices: lineSpanIndices,
    };
  }

  // --- Document title (virtual block) ---
  // The title renders at the top of page 1 (uppercased). Match defensively:
  // the span text must be contained in the title AND the expanded line(s) must
  // be highly similar to it — so a body word that merely echoes a title word
  // doesn't get misrouted to title editing.
  const titleText = (document.metadata?.title || '').trim();
  if (titleText) {
    const titleNorm = titleText.toLowerCase().replace(/\s+/g, ' ').trim();
    const spanNorm = spanText.toLowerCase().replace(/\s+/g, ' ').trim();
    if (spanNorm.length >= 4 && titleNorm.includes(spanNorm)) {
      const { matchingSpanIndices, combinedNormalized } = expandParagraph(allSpans, spanIndex);
      const titleNormStripped = titleNorm.replace(/[-­]/g, '');
      if (calculateSimilarity(combinedNormalized, titleNormStripped) >= 0.6) {
        return {
          blockMapping: {
            blockId: 'metadata-title',
            blockPath: [],
            text: titleText,
            type: 'paragraph',
            isLeaf: true,
            isTitle: true,
          },
          matchingSpanIndices,
        };
      }
    }
  }

  // --- Standard block matching ---
  const blockMapping = findBlockByText(spanText, document);
  if (!blockMapping) return null;

  const blockTextNormalized = blockMapping.text.toLowerCase().replace(/\s+/g, ' ').trim();
  const { matchingSpanIndices, combinedNormalized } = expandParagraph(allSpans, spanIndex);
  const similarity = calculateSimilarity(combinedNormalized, blockTextNormalized);

  // If the expansion crossed into an adjacent paragraph, fall back to the span.
  if (similarity < 0.3 && matchingSpanIndices.length > 5) {
    return { blockMapping, matchingSpanIndices: [spanIndex] };
  }

  return { blockMapping, matchingSpanIndices };
}

/**
 * Expand a span into its full visual paragraph using Y-position continuity
 * (robust against PDF.js hyphenated fragments), returning the member span
 * indices and the combined, hyphen-stripped normalized text.
 */
function expandParagraph(
  allSpans: HTMLElement[],
  spanIndex: number
): { matchingSpanIndices: number[]; combinedNormalized: string } {
  const currentRect = allSpans[spanIndex].getBoundingClientRect();
  const lineHeight = currentRect.height > 0 ? currentRect.height * 2.2 : 24;

  let startIdx = spanIndex;
  let endIdx = spanIndex;

  let prevBottom = currentRect.bottom;
  for (let i = spanIndex - 1; i >= 0; i--) {
    const text = (allSpans[i].textContent || '').trim();
    if (!text) continue;
    const rect = allSpans[i].getBoundingClientRect();
    if (prevBottom - rect.bottom > lineHeight) break;
    startIdx = i;
    prevBottom = rect.bottom;
  }

  let prevTop = currentRect.top;
  for (let i = spanIndex + 1; i < allSpans.length; i++) {
    const text = (allSpans[i].textContent || '').trim();
    if (!text) continue;
    const rect = allSpans[i].getBoundingClientRect();
    if (rect.top - prevTop > lineHeight) break;
    endIdx = i;
    prevTop = rect.top;
  }

  const matchingSpanIndices: number[] = [];
  let combinedText = '';
  for (let i = startIdx; i <= endIdx; i++) {
    const text = (allSpans[i].textContent || '').trim();
    if (text) {
      combinedText += (combinedText ? ' ' : '') + text;
      matchingSpanIndices.push(i);
    }
  }

  const combinedNormalized = combinedText
    .toLowerCase()
    .replace(/[-­]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  return { matchingSpanIndices, combinedNormalized };
}

/** Normalize text for substring search: lowercase, collapse ws, strip hyphens. */
function normalizeForSearch(text: string): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[-­]/g, '')
    .trim();
}

/**
 * Find the span indices that render a given target string within a page's text
 * layer. Concatenates spans (rejoining hyphenated line breaks) and substring-
 * matches the target, then maps the hit back to the covering spans. Used to
 * locate "virtual" blocks (title, signature fields) that aren't in the content
 * tree, so they can be scrolled to and flash-highlighted from the outline.
 */
function locateSpansByText(allSpans: HTMLElement[], targetText: string): number[] | null {
  const target = normalizeForSearch(targetText);
  if (target.length < 2) return null;

  let combined = '';
  const ranges: { spanIdx: number; start: number; end: number }[] = [];
  for (let i = 0; i < allSpans.length; i++) {
    const raw = (allSpans[i].textContent || '').toLowerCase().replace(/\s+/g, ' ').trim();
    if (!raw) continue;
    const endsHyphen = /[-­]$/.test(raw);
    const norm = raw.replace(/[-­]/g, '');
    if (!norm) continue;
    const start = combined.length;
    combined += norm;
    ranges.push({ spanIdx: i, start, end: combined.length });
    combined += endsHyphen ? '' : ' ';
  }

  let at = combined.indexOf(target);
  let matchLen = target.length;
  // Long values (e.g. the attestation clause) may differ in trailing
  // punctuation; fall back to matching a distinctive prefix.
  if (at < 0 && target.length > 24) {
    const prefix = target.slice(0, 24);
    at = combined.indexOf(prefix);
    matchLen = prefix.length;
  }
  if (at < 0) return null;

  const matchEnd = at + matchLen;
  const indices = ranges.filter((r) => r.start < matchEnd && r.end > at).map((r) => r.spanIdx);
  return indices.length ? indices : null;
}

/**
 * Compute a clean bounding rectangle from a set of span indices.
 * Groups spans into visual rows (by Y-position clustering) and normalises
 * to a single straight-edged rectangle using the widest row's extent.
 */
function computeCleanBoundingRect(
  spanIndices: number[],
  allSpans: HTMLElement[],
  pageRect: DOMRect
): { x: number; y: number; width: number; height: number } | null {
  if (spanIndices.length === 0) return null;

  const rects: { left: number; top: number; right: number; bottom: number }[] = [];
  for (const idx of spanIndices) {
    const span = allSpans[idx];
    if (!span) continue;
    const r = span.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    rects.push({
      left: r.left - pageRect.left,
      top: r.top - pageRect.top,
      right: r.right - pageRect.left,
      bottom: r.bottom - pageRect.top,
    });
  }

  if (rects.length === 0) return null;

  // Group into visual rows — spans within ~6px of each other vertically
  rects.sort((a, b) => a.top - b.top);
  const rows: (typeof rects)[] = [];
  let currentRow: typeof rects = [rects[0]];

  for (let i = 1; i < rects.length; i++) {
    const rowMidY = currentRow[0].top + (currentRow[0].bottom - currentRow[0].top) / 2;
    const spanMidY = rects[i].top + (rects[i].bottom - rects[i].top) / 2;
    if (Math.abs(spanMidY - rowMidY) < 6) {
      currentRow.push(rects[i]);
    } else {
      rows.push(currentRow);
      currentRow = [rects[i]];
    }
  }
  rows.push(currentRow);

  const rowBounds = rows.map((row) => ({
    left: Math.min(...row.map((r) => r.left)),
    right: Math.max(...row.map((r) => r.right)),
    top: Math.min(...row.map((r) => r.top)),
    bottom: Math.max(...row.map((r) => r.bottom)),
  }));

  const globalLeft = Math.min(...rowBounds.map((r) => r.left));
  const globalRight = Math.max(...rowBounds.map((r) => r.right));
  const globalTop = rowBounds[0].top;
  const globalBottom = rowBounds[rowBounds.length - 1].bottom;

  const padding = 8;
  return {
    x: Math.max(0, globalLeft - padding),
    y: Math.max(0, globalTop - padding),
    width: globalRight - globalLeft + padding * 2,
    height: globalBottom - globalTop + padding * 2,
  };
}

export function useBlockHover({
  document,
  pageRefs,
  effectiveDateLabel,
  enabled = true,
  totalPages = null,
}: UseBlockHoverOptions) {
  const [hoveredBlock, setHoveredBlock] = useState<ReviewHighlight | null>(null);

  // The signature page (always the last page, after a forced break) is edited
  // via the outline only — hovering its text layer would otherwise resolve to
  // the wrong content block.
  const isSignaturePage = useCallback(
    (pageNumber: number) =>
      totalPages != null && !!document.signatories?.length && pageNumber === totalPages,
    [totalPages, document]
  );

  const rafIdRef = useRef<number | null>(null);
  const lastBlockIdRef = useRef<string | null>(null);
  const pendingClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Memoize text index so it rebuilds only when the document changes.
  // (Currently used to keep parity with the source; expansion uses the DOM.)
  useMemo(() => buildDocumentTextIndex(document), [document]);

  const clearHover = useCallback(() => {
    setHoveredBlock(null);
    lastBlockIdRef.current = null;
    if (pendingClearRef.current) {
      clearTimeout(pendingClearRef.current);
      pendingClearRef.current = null;
    }
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, pageNumber: number) => {
      if (!enabled) return;

      // Suppress hover on the signature page (edited via the outline instead).
      if (isSignaturePage(pageNumber)) {
        if (lastBlockIdRef.current !== null) {
          setHoveredBlock(null);
          lastBlockIdRef.current = null;
        }
        return;
      }

      if (pendingClearRef.current) {
        clearTimeout(pendingClearRef.current);
        pendingClearRef.current = null;
      }

      // Capture target before rAF — React synthetic events are pooled
      const target = event.target as HTMLElement;

      // Throttle to one computation per animation frame
      if (rafIdRef.current) return;

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;

        const textLayer = target.closest('.react-pdf__Page__textContent');
        if (!textLayer) {
          if (!pendingClearRef.current) {
            pendingClearRef.current = setTimeout(() => {
              pendingClearRef.current = null;
              setHoveredBlock(null);
              lastBlockIdRef.current = null;
            }, 200);
          }
          return;
        }

        const textSpan = target.closest('span');
        if (!textSpan) return;

        const pageElement = pageRefs.current?.get(pageNumber);
        if (!pageElement) return;

        const textLayerElement = pageElement.querySelector('.react-pdf__Page__textContent');
        if (!textLayerElement) return;

        const allSpans = Array.from(textLayerElement.querySelectorAll('span')) as HTMLElement[];
        const spanIndex = allSpans.indexOf(textSpan as HTMLElement);
        if (spanIndex === -1) return;

        const spanText = (textSpan.textContent || '').trim();
        const result = findBlockForSpan(spanText, allSpans, spanIndex, document, effectiveDateLabel);

        if (!result) {
          if (!pendingClearRef.current) {
            pendingClearRef.current = setTimeout(() => {
              pendingClearRef.current = null;
              setHoveredBlock(null);
              lastBlockIdRef.current = null;
            }, 200);
          }
          return;
        }

        const { blockMapping, matchingSpanIndices } = result;

        // Block ID dedup — skip DOM measurement if we're still on the same block
        if (lastBlockIdRef.current === blockMapping.blockId) return;
        lastBlockIdRef.current = blockMapping.blockId;

        const pageRect = pageElement.getBoundingClientRect();
        const boundingRect = computeCleanBoundingRect(matchingSpanIndices, allSpans, pageRect);
        if (!boundingRect) return;

        setHoveredBlock({ block: toReviewBlock(blockMapping), pageNumber, rect: boundingRect });
      });
    },
    [document, pageRefs, effectiveDateLabel, enabled, isSignaturePage]
  );

  const handleMouseLeave = useCallback(() => {
    // Grace period — allow re-entry without flicker
    if (pendingClearRef.current) clearTimeout(pendingClearRef.current);
    pendingClearRef.current = setTimeout(() => {
      pendingClearRef.current = null;
      setHoveredBlock(null);
      lastBlockIdRef.current = null;
    }, 200);
  }, []);

  /**
   * Resolve a block from a touch target element (for tap-to-edit on mobile).
   */
  const resolveBlockFromTouch = useCallback(
    (target: HTMLElement, pageNumber: number): ReviewHighlight | null => {
      if (!enabled) return null;
      if (isSignaturePage(pageNumber)) return null;
      const textSpan = target.closest('span');
      if (!textSpan) return null;

      const pageElement = pageRefs.current?.get(pageNumber);
      if (!pageElement) return null;

      const textLayerElement = pageElement.querySelector('.react-pdf__Page__textContent');
      if (!textLayerElement) return null;

      const allSpans = Array.from(textLayerElement.querySelectorAll('span')) as HTMLElement[];
      const spanIndex = allSpans.indexOf(textSpan as HTMLElement);
      if (spanIndex === -1) return null;

      const spanText = (textSpan.textContent || '').trim();
      const result = findBlockForSpan(spanText, allSpans, spanIndex, document, effectiveDateLabel);
      if (!result) return null;

      const { blockMapping, matchingSpanIndices } = result;
      const pageRect = pageElement.getBoundingClientRect();
      const boundingRect = computeCleanBoundingRect(matchingSpanIndices, allSpans, pageRect);
      if (!boundingRect) return null;

      return { block: toReviewBlock(blockMapping), pageNumber, rect: boundingRect };
    },
    [document, pageRefs, effectiveDateLabel, enabled, isSignaturePage]
  );

  /**
   * Locate a block in the *rendered* PDF from its mapping — the inverse of the
   * hover flow (which goes span → block). Scans each painted page's text layer
   * for a span that resolves to the target block, then returns its page number
   * + bounding rect so the caller can scroll to and flash-highlight it.
   *
   * Returns null if the block isn't found on any currently-painted page.
   */
  const locateBlock = useCallback(
    (block: ReviewBlock): ReviewHighlight | null => {
      const mapping = fromReviewBlock(block);
      if (!pageRefs.current) return null;
      const pages = Array.from(pageRefs.current.keys()).sort((a, b) => a - b);

      for (const pageNumber of pages) {
        const pageElement = pageRefs.current.get(pageNumber);
        if (!pageElement) continue;

        const textLayerElement = pageElement.querySelector('.react-pdf__Page__textContent');
        if (!textLayerElement) continue;

        const allSpans = Array.from(textLayerElement.querySelectorAll('span')) as HTMLElement[];
        if (allSpans.length === 0) continue;

        // Virtual blocks (title, signature fields) aren't in the content tree,
        // so locate them by matching their value text directly in the layer.
        if (isVirtualBlockId(mapping.blockId)) {
          const indices = locateSpansByText(allSpans, mapping.text);
          if (indices) {
            const pageRect = pageElement.getBoundingClientRect();
            const boundingRect = computeCleanBoundingRect(indices, allSpans, pageRect);
            if (boundingRect) return { block, pageNumber, rect: boundingRect };
          }
          continue;
        }

        for (let i = 0; i < allSpans.length; i++) {
          const spanText = (allSpans[i].textContent || '').trim();
          if (spanText.length < 2) continue;

          // Cheap text→block check first (pure string work, no DOM measurement).
          // Only when a span points at the target block do we run the expensive
          // span-expansion + getBoundingClientRect path below.
          const isCandidate = findBlockByText(spanText, document)?.blockId === mapping.blockId;
          if (!isCandidate) continue;

          const result = findBlockForSpan(spanText, allSpans, i, document, effectiveDateLabel);
          if (result && result.blockMapping.blockId === mapping.blockId) {
            const pageRect = pageElement.getBoundingClientRect();
            const boundingRect = computeCleanBoundingRect(
              result.matchingSpanIndices,
              allSpans,
              pageRect
            );
            if (!boundingRect) return null;
            return { block, pageNumber, rect: boundingRect };
          }
        }
      }

      return null;
    },
    [document, pageRefs, effectiveDateLabel]
  );

  return {
    hoveredBlock,
    handleMouseMove,
    handleMouseLeave,
    clearHover,
    resolveBlockFromTouch,
    locateBlock,
  };
}
